import { useEffect, useRef } from 'react'

/** The one way an interactive widget reports its on-screen task; string-only so activityData's answer key
 *  cannot ship. `expectedAnswer` travels in the same dispatch because the slice replaces extras whole.
 *  A task that goes away after being emitted clears the instance, so a taskless round cannot keep the last one live. */
export const useEmitInstance = (
  onInstanceChange: ((task: string | null, expectedAnswer?: string, facts?: string[]) => void) | undefined,
  task: string | null,
  expectedAnswer?: string | null,
  facts?: string[] | null,
): void => {
  const hadTask = useRef(false)
  // Joined key: a fresh array each render must not re-dispatch an unchanged instance.
  const factsKey = facts?.length ? facts.join('\u001f') : undefined
  useEffect(() => {
    if (task) {
      hadTask.current = true
      onInstanceChange?.(task, expectedAnswer ?? undefined, factsKey?.split('\u001f'))
    } else if (hadTask.current) {
      hadTask.current = false
      onInstanceChange?.(null)
    }
  }, [task, expectedAnswer, factsKey, onInstanceChange])
}

/** A static-picture widget reports its asset URL instead of a text task, which would be the answer. */
export const useEmitInstanceImage = (
  onInstanceImageChange: ((imageUrl: string) => void) | undefined,
  imageUrl: string | null,
): void => {
  useEffect(() => {
    if (imageUrl) onInstanceImageChange?.(imageUrl)
  }, [imageUrl, onInstanceImageChange])
}
